import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Swiper from "swiper";
import { Pagination } from "swiper/modules"; // Removed Navigation
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import type { TransformedProduct } from "../../../types/productApiTypes";

import Icon from "../../../../../../common/components/Icon/Icon";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";

// Import Lightbox and Plugin styles
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

interface ProductInfoGalleryProps {
  product: TransformedProduct;
  selectedVariation: TransformedProduct | null;
}

const ProductInfoGallery: React.FC<ProductInfoGalleryProps> = ({
  product,
  selectedVariation,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const swiperRef = useRef<HTMLDivElement>(null);

  const [lightboxIndex, setLightboxIndex] = useState<number | undefined>(
    undefined
  );

  // Combine Variation Images with Parent Gallery Images
  const imagesToShow = useMemo(() => {
    // 1. If no variation is selected, simply return the parent product images.
    if (!selectedVariation) {
      return product.images;
    }

    // 2. Get Variation Images
    // This includes the specific variation image + any custom variation gallery images
    // (via the backend extension you already implemented).
    const varImages = selectedVariation.images || [];

    // 3. Get Parent Images
    // Filter out images that are already present in the variation set (avoid duplicates by ID)
    const varImageIds = new Set(varImages.map((img) => img.id));
    const uniqueParentImages = product.images.filter(
      (img) => !varImageIds.has(img.id)
    );

    // 4. Return Combined Array: Variation Images First -> Then Parent Images
    return [...varImages, ...uniqueParentImages];
  }, [product.images, selectedVariation]);

  const slides = imagesToShow.map((image) => ({
    src: image.src,
    alt: image.alt || product.name,
  }));

  useEffect(() => {
    let mainSlider: Swiper | null = null;
    if (swiperRef.current) {
      mainSlider = new Swiper(swiperRef.current, {
        modules: [Pagination],
        direction: "horizontal",
        loop: true,
        spaceBetween: 0,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
      });
    }

    return () => {
      mainSlider?.destroy();
    };
  }, [imagesToShow]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
  };

  if (!areTranslationsReady) {
    return null;
  }

  return (
    <>
      <div className="product-info-gallery">
        <div className="swiper main-swiper" ref={swiperRef}>
          <div className="swiper-wrapper">
            {imagesToShow.map((image, index) => (
              <div
                key={image.id}
                className="swiper-slide"
                onClick={() => openLightbox(index)}
                style={{ cursor: "pointer" }}
              >
                <img
                  src={image.thumbnail}
                  data-srcset={image.srcset}
                  data-sizes="auto"
                  className="lazyload"
                  alt={image.alt || product.name}
                />
                <button
                  className="product-info-gallery__zoom-icon"
                  aria-label={t(
                    "productInfoGallery.enlargeImage",
                    "Enlarge image"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox(index);
                  }}
                >
                  <Icon
                    iconName="fullscreen-outlined"
                    className="icon--stroke-thick"
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="swiper-pagination"></div>
        </div>
      </div>

      <Lightbox
        open={lightboxIndex !== undefined}
        close={() => setLightboxIndex(undefined)}
        slides={slides}
        index={lightboxIndex}
        plugins={[Thumbnails, Zoom]}
        zoom={{
          maxZoomPixelRatio: 3,
        }}
        thumbnails={{
          border: 0,
          padding: 2,
          gap: 8,
        }}
      />
    </>
  );
};

export default ProductInfoGallery;
